import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

const AlertDialogContext = createContext(null);

/** Duration (ms) kept in sync with the transition classes below for exit animation. */
const TRANSITION_MS = 200;

/**
 * Controlled alert dialog (shadcn/ui-style) with smooth enter/exit transitions.
 * The node stays mounted for one transition after `open` flips to false so the
 * fade/zoom-out can play before unmount.
 * @param {{ open: boolean, onOpenChange?: (open: boolean) => void, children?: React.ReactNode }} props
 */
export function AlertDialog({ open, onOpenChange, children }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Next frame: flip to visible so the entry transition runs from the
      // hidden state instead of being skipped on first paint.
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const timer = setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <AlertDialogContext.Provider value={{ onOpenChange, visible }}>
      <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
        <button
          type="button"
          aria-label="Close dialog"
          className={cn(
            'fixed inset-0 bg-black/50 transition-opacity duration-200 ease-out motion-reduce:transition-none',
            visible ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => onOpenChange?.(false)}
        />
        {children}
      </div>
    </AlertDialogContext.Provider>,
    document.body,
  );
}

export function AlertDialogContent({ className, children }) {
  const { visible } = useAlertDialog();
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className={cn(
        'relative z-[1101] grid w-full max-w-lg gap-4 rounded-lg border border-slate-200 bg-white p-6 shadow-lg',
        'transition-all duration-200 ease-out motion-reduce:transition-none',
        visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-1',
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

export function AlertDialogHeader({ className, children }) {
  return <div className={cn('flex flex-col gap-2 text-center sm:text-left', className)}>{children}</div>;
}

export function AlertDialogTitle({ className, children }) {
  return <h2 className={cn('text-base font-semibold text-slate-900', className)}>{children}</h2>;
}

export function AlertDialogDescription({ className, children }) {
  return <p className={cn('text-sm text-slate-500', className)}>{children}</p>;
}

export function AlertDialogFooter({ className, children }) {
  return (
    <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}>
      {children}
    </div>
  );
}

function useAlertDialog() {
  const ctx = useContext(AlertDialogContext);
  return ctx ?? { onOpenChange: () => {}, visible: true };
}

export function AlertDialogCancel({ className, children, onClick, ...props }) {
  const { onOpenChange } = useAlertDialog();
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700',
        'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2',
        className,
      )}
      onClick={(e) => {
        onClick?.(e);
        onOpenChange(false);
      }}
      {...props}
    >
      {children ?? 'Cancel'}
    </button>
  );
}

export function AlertDialogAction({ className, children, onClick, ...props }) {
  const { onOpenChange } = useAlertDialog();
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-medium text-white',
        'hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
        className,
      )}
      onClick={(e) => {
        onClick?.(e);
        onOpenChange(false);
      }}
      {...props}
    >
      {children ?? 'Continue'}
    </button>
  );
}
